import { useState } from "react";

/**
 * A credential that should not sit in plain view.
 *
 * The domain's private key and webhook secret used to render through <Fields>
 * like any other string, fully expanded on screen. They are shown masked here,
 * revealed only on request, with a copy button so the common case (paste it
 * somewhere else) never needs the reveal at all.
 *
 * The markup mirrors what <Fields> emits for a read-only field — ml-2 wrapper,
 * dl.row, dt.col-sm-5, dd.col-sm-7 — so these two rows line up with the plain
 * fields above them instead of reading as a separate block. The dl carries
 * mb-0 because <Fields> puts every field in one dl, and only dd's own margin
 * separates the rows there.
 */
export const Secret = ({ label, value }) => {
  const [shown, setShown] = useState(false);
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      // Clipboard is unavailable outside a secure context — reveal instead,
      // so there is still a way to get at the value.
      setShown(true);
    }
  };

  if (!value) {
    return null;
  }

  return (
    <div className="ml-2">
      <dl className="row mb-0">
        <dt className="col-sm-5">{label}</dt>
        <dd className="col-sm-7">
          <span style={{ wordBreak: "break-all" }}>
            {shown ? value : "•".repeat(Math.min(value.length, 32))}
          </span>{" "}
          <button
            type="button"
            className="btn btn-link btn-sm p-0 align-baseline"
            onClick={() => setShown(!shown)}
          >
            {shown ? "Hide" : "Show"}
          </button>{" "}
          <button
            type="button"
            className="btn btn-link btn-sm p-0 align-baseline"
            onClick={copy}
          >
            {copied ? "Copied" : "Copy"}
          </button>
        </dd>
      </dl>
    </div>
  );
};
